import { useEffect, useRef, useState } from 'react';
import { X, Megaphone, AlertTriangle, Info, Zap } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

/**
 * Global Broadcast News Ticker — shown at the top of protected layouts.
 * Auto-refreshes every 2 minutes. Users can dismiss the ticker per-session (sessionStorage).
 * Only renders items where show_ticker is true.
 */

const PRIORITY_STYLES = {
    info: { bg: 'bg-blue-600', text: 'text-white', Icon: Info },
    warning: { bg: 'bg-amber-500', text: 'text-white', Icon: AlertTriangle },
    urgent: { bg: 'bg-red-600', text: 'text-white', Icon: Zap },
};

export function BroadcastTicker() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [dismissed, setDismissed] = useState(false);
    const [idx, setIdx] = useState(0);
    const timer = useRef(null);

    useEffect(() => {
        if (!user) return;
        if (typeof window !== 'undefined' && sessionStorage.getItem('bn_ticker_dismissed') === '1') {
            setDismissed(true);
        }
        let cancelled = false;
        const load = async () => {
            try {
                const r = await api.broadcastNewsActive();
                if (!cancelled) {
                    const visible = (r.items || []).filter(i => i.show_ticker !== false);
                    setItems(visible);
                    setIdx(0);
                }
            } catch { /* silent */ }
        };
        load();
        const iv = setInterval(load, 120000); // 2 minutes
        return () => { cancelled = true; clearInterval(iv); };
    }, [user]);

    // Auto-cycle multiple items every 8 seconds
    useEffect(() => {
        if (items.length <= 1) return;
        if (timer.current) clearInterval(timer.current);
        timer.current = setInterval(() => {
            setIdx(i => (i + 1) % items.length);
        }, 8000);
        return () => { if (timer.current) clearInterval(timer.current); };
    }, [items.length]);

    if (!user || dismissed || items.length === 0) return null;

    const active = items[idx] || items[0];
    const style = PRIORITY_STYLES[active.priority] || PRIORITY_STYLES.info;
    const { Icon } = style;

    const handleDismiss = () => {
        try { sessionStorage.setItem('bn_ticker_dismissed', '1'); } catch { /* ignore */ }
        setDismissed(true);
    };

    return (
        <div
            className={`${style.bg} ${style.text} shadow-sm relative z-40`}
            data-testid='broadcast-ticker'
            role='alert'
            aria-live='polite'
        >
            <div className='max-w-full mx-auto px-4 py-2 flex items-center gap-3 text-sm'>
                <Megaphone className='w-4 h-4 flex-shrink-0 opacity-90' />
                <div className='flex-1 overflow-hidden'>
                    <div className='flex items-center gap-2 min-w-0'>
                        <Icon className='w-3.5 h-3.5 flex-shrink-0 opacity-80' />
                        <span className='font-semibold whitespace-nowrap' data-testid='broadcast-ticker-title'>
                            {active.title}
                        </span>
                        {active.body && (
                            <span className='opacity-95 truncate' data-testid='broadcast-ticker-body'>
                                — {active.body}
                            </span>
                        )}
                    </div>
                </div>
                {items.length > 1 && (
                    <div className='flex items-center gap-1 flex-shrink-0'>
                        {items.map((_, i) => (
                            <button
                                key={i}
                                type='button'
                                onClick={() => setIdx(i)}
                                aria-label={`Show item ${i + 1}`}
                                className={`w-1.5 h-1.5 rounded-full transition-opacity ${i === idx ? 'opacity-100 bg-white' : 'opacity-40 bg-white hover:opacity-70'}`}
                            />
                        ))}
                    </div>
                )}
                <button
                    type='button'
                    onClick={handleDismiss}
                    aria-label='Dismiss'
                    className='flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity'
                    data-testid='broadcast-ticker-dismiss'
                >
                    <X className='w-4 h-4' />
                </button>
            </div>
        </div>
    );
}

/**
 * Dashboard highlight cards — richer version, shown on dashboards.
 * Only renders items where show_card is true.
 */
export function BroadcastCards({ className = '' }) {
    const { user } = useAuth();
    const [items, setItems] = useState([]);

    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        const load = async () => {
            try {
                const r = await api.broadcastNewsActive();
                if (!cancelled) setItems((r.items || []).filter(i => i.show_card !== false));
            } catch { /* silent */ }
        };
        load();
        const iv = setInterval(load, 120000);
        return () => { cancelled = true; clearInterval(iv); };
    }, [user]);

    if (!user || items.length === 0) return null;

    return (
        <div className={`space-y-3 ${className}`} data-testid='broadcast-cards'>
            {items.map(item => {
                const style = PRIORITY_STYLES[item.priority] || PRIORITY_STYLES.info;
                const { Icon } = style;
                return (
                    <div
                        key={item.id}
                        className={`rounded-lg border shadow-sm p-4 flex items-start gap-3 ${item.priority === 'urgent'
                            ? 'border-red-200 bg-red-50'
                            : item.priority === 'warning'
                                ? 'border-amber-200 bg-amber-50'
                                : 'border-blue-200 bg-blue-50'}`}
                        data-testid={`broadcast-card-${item.id}`}
                    >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${style.bg} ${style.text}`}>
                            <Icon className='w-5 h-5' />
                        </div>
                        <div className='flex-1 min-w-0'>
                            <div className='font-semibold text-sm mb-1' data-testid={`broadcast-card-title-${item.id}`}>{item.title}</div>
                            {item.body && (
                                <div className='text-sm text-muted-foreground whitespace-pre-wrap' data-testid={`broadcast-card-body-${item.id}`}>{item.body}</div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
